import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings,
  Link2,
  Monitor,
  Clock,
  Users,
  ListOrdered,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
  Zap,
  Mail,
} from 'lucide-react';
import type {
  Team,
  TeamMember,
  SalesUserSettings,
  AssignmentRule,
  AssignmentRuleConditions,
  TaskTemplate,
  TaskTemplateStep,
  ContactType,
} from '../../types';
import type { InstantlyList, InstantlyCampaign } from '../../services/instantly';
import {
  getUserSettings,
  upsertUserSettings,
  getAssignmentRules,
  createAssignmentRule,
  updateAssignmentRule,
  deleteAssignmentRule,
} from '../../services/settings';
import {
  getTaskTemplates,
  createTaskTemplate,
  updateTaskTemplate,
  deleteTaskTemplate,
  seedDefaultTemplate,
} from '../../services/sequences';
import {
  getInstantlyLists,
  getInstantlyCampaigns,
  getInstantlyOrgSettings,
  saveInstantlyOrgSettings,
} from '../../services/instantly';
import { getTeamMembers } from '../../services/teams';
import { CONTACT_TYPES, SEQUENCE_CHANNELS } from '../../constants';

interface SettingsPageProps {
  team: Team;
  userId: string;
}

const SettingsPage: React.FC<SettingsPageProps> = ({ team, userId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState<TeamMember[]>([]);

  const [settings, setSettings] = useState<SalesUserSettings | null>(null);
  const [bookingUrl, setBookingUrl] = useState('');
  const [demoEnvUrl, setDemoEnvUrl] = useState('');
  const [timezone, setTimezone] = useState('Europe/London');

  const [rules, setRules] = useState<AssignmentRule[]>([]);
  const [editingRule, setEditingRule] = useState<string | null>(null);
  const [newRule, setNewRule] = useState<{
    name: string;
    conditions: AssignmentRuleConditions;
    assign_to_user_id: string;
  } | null>(null);

  const [templates, setTemplates] = useState<TaskTemplate[]>([]);
  const [expandedTemplate, setExpandedTemplate] = useState<string | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [newTemplate, setNewTemplate] = useState<{
    name: string;
    description: string;
    steps: TaskTemplateStep[];
  } | null>(null);

  // Instantly integration state
  const [instantlyLists, setInstantlyLists] = useState<InstantlyList[]>([]);
  const [instantlyCampaigns, setInstantlyCampaigns] = useState<InstantlyCampaign[]>([]);
  const [instantlyDefaultList, setInstantlyDefaultList] = useState('');
  const [instantlyDefaultCampaign, setInstantlyDefaultCampaign] = useState('');
  const [savingInstantly, setSavingInstantly] = useState(false);
  const [loadingInstantly, setLoadingInstantly] = useState(false);

  const fetchData = useCallback(async () => {
    const [settingsData, membersData, rulesData, templatesData, instantlySettings] = await Promise.all([
      getUserSettings(userId),
      getTeamMembers(team.id),
      getAssignmentRules(team.id),
      getTaskTemplates(team.id),
      getInstantlyOrgSettings(team.id),
    ]);

    setSettings(settingsData);
    setBookingUrl(settingsData?.booking_url || '');
    setDemoEnvUrl(settingsData?.demo_env_url || '');
    setTimezone(settingsData?.timezone || 'Europe/London');
    setMembers(membersData);
    setRules(rulesData);
    setTemplates(templatesData);
    setInstantlyDefaultList(instantlySettings?.default_lead_list_id || '');
    setInstantlyDefaultCampaign(instantlySettings?.default_campaign_id || '');
  }, [userId, team.id]);

  const fetchInstantlyOptions = useCallback(async () => {
    setLoadingInstantly(true);
    const [lists, campaigns] = await Promise.all([
      getInstantlyLists(),
      getInstantlyCampaigns(),
    ]);
    setInstantlyLists(lists);
    setInstantlyCampaigns(campaigns);
    setLoadingInstantly(false);
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  useEffect(() => {
    fetchInstantlyOptions();
  }, [fetchInstantlyOptions]);

  const handleSaveInstantly = async () => {
    setSavingInstantly(true);
    await saveInstantlyOrgSettings(
      team.id,
      instantlyDefaultList || null,
      instantlyDefaultCampaign || null
    );
    setSavingInstantly(false);
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    await upsertUserSettings(userId, team.id, {
      booking_url: bookingUrl || null,
      demo_env_url: demoEnvUrl || null,
      timezone,
    });
    setSaving(false);
  };

  const handleSaveRule = async (ruleId: string, updates: Partial<AssignmentRule>) => {
    await updateAssignmentRule(ruleId, updates);
    setEditingRule(null);
    fetchData();
  };

  const handleCreateRule = async () => {
    if (!newRule || !newRule.name || !newRule.assign_to_user_id) return;
    await createAssignmentRule(team.id, newRule.name, newRule.conditions, newRule.assign_to_user_id);
    setNewRule(null);
    fetchData();
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!confirm('Delete this assignment rule?')) return;
    await deleteAssignmentRule(ruleId);
    fetchData();
  };

  const handleSaveTemplate = async (templateId: string, updates: Partial<TaskTemplate>) => {
    await updateTaskTemplate(templateId, updates);
    setEditingTemplate(null);
    fetchData();
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate || !newTemplate.name) return;
    await createTaskTemplate(team.id, newTemplate.name, newTemplate.description || null, newTemplate.steps);
    setNewTemplate(null);
    fetchData();
  };

  const handleDeleteTemplate = async (templateId: string) => {
    if (!confirm('Delete this sequence template?')) return;
    await deleteTaskTemplate(templateId);
    fetchData();
  };

  const handleSeedDefault = async () => {
    await seedDefaultTemplate(team.id);
    fetchData();
  };

  const getMemberEmail = (id: string) => {
    const m = members.find((m) => m.user_id === id);
    return m?.email || id.slice(0, 8);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <Settings size={24} className="text-orange-500" />
          Settings
        </h1>
        <p className="text-slate-400 text-sm mt-1">Configure your sales tools and automation</p>
      </div>

      {/* Personal Settings */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Personal Settings</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              <Link2 size={14} className="inline mr-1.5" />
              Booking URL
            </label>
            <input
              type="url"
              value={bookingUrl}
              onChange={(e) => setBookingUrl(e.target.value)}
              placeholder="https://calendly.com/your-link"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <p className="text-xs text-slate-500 mt-1">Used for "Copy Booking Message" in contact drawer</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              <Monitor size={14} className="inline mr-1.5" />
              Demo Environment URL
            </label>
            <input
              type="url"
              value={demoEnvUrl}
              onChange={(e) => setDemoEnvUrl(e.target.value)}
              placeholder="https://demo.rehub.studio"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <p className="text-xs text-slate-500 mt-1">Opens when you click "Demo Mode" in deal drawer</p>
          </div>

          <div className="w-1/2">
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              <Clock size={14} className="inline mr-1.5" />
              Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="Europe/London">Europe/London</option>
              <option value="America/New_York">America/New York</option>
              <option value="America/Los_Angeles">America/Los Angeles</option>
              <option value="Europe/Paris">Europe/Paris</option>
              <option value="Asia/Tokyo">Asia/Tokyo</option>
            </select>
          </div>

          <div className="pt-4">
            <button
              onClick={handleSaveSettings}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-600 text-white font-medium rounded-lg transition-colors"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save Settings
            </button>
          </div>
        </div>
      </div>

      {/* Assignment Rules */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Users size={20} className="text-orange-500" />
            Assignment Rules
          </h2>
          <button
            onClick={() => setNewRule({ name: '', conditions: {}, assign_to_user_id: '' })}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
          >
            <Plus size={16} />
            Add Rule
          </button>
        </div>
        <p className="text-sm text-slate-400 mb-4">
          Automatically assign new leads to team members based on conditions
        </p>

        <div className="space-y-3">
          {rules.map((rule) => (
            <div key={rule.id} className="p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
              {editingRule === rule.id ? (
                <RuleEditor
                  rule={rule}
                  members={members}
                  onSave={(updates) => handleSaveRule(rule.id, updates)}
                  onCancel={() => setEditingRule(null)}
                />
              ) : (
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-white">{rule.name}</div>
                    <div className="text-xs text-slate-400 mt-1">
                      {rule.conditions.type ? `Type: ${rule.conditions.type}` : 'All types'}
                      {rule.conditions.source ? ` • Source: ${rule.conditions.source}` : ''}
                      {' → '}
                      {getMemberEmail(rule.assign_to_user_id)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSaveRule(rule.id, { enabled: !rule.enabled })}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        rule.enabled
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-slate-700 text-slate-400'
                      }`}
                    >
                      {rule.enabled ? 'Enabled' : 'Disabled'}
                    </button>
                    <button
                      onClick={() => setEditingRule(rule.id)}
                      className="p-1.5 text-slate-400 hover:text-white transition-colors"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule.id)}
                      className="p-1.5 text-slate-400 hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {newRule && (
            <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <RuleEditor
                rule={{
                  id: 'new',
                  team_id: team.id,
                  name: newRule.name,
                  conditions: newRule.conditions,
                  assign_to_user_id: newRule.assign_to_user_id,
                  priority: 0,
                  enabled: true,
                  created_at: '',
                  updated_at: '',
                }}
                members={members}
                onSave={() => handleCreateRule()}
                onCancel={() => setNewRule(null)}
                onChange={(updates) => setNewRule({ ...newRule, ...updates })}
                isNew
              />
            </div>
          )}

          {rules.length === 0 && !newRule && (
            <p className="text-sm text-slate-400 text-center py-4">
              No assignment rules configured
            </p>
          )}
        </div>
      </div>

      {/* Task Sequences */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <ListOrdered size={20} className="text-orange-500" />
            Task Sequences
          </h2>
          <div className="flex items-center gap-2">
            {templates.length === 0 && (
              <button
                onClick={handleSeedDefault}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
              >
                Seed Default
              </button>
            )}
            <button
              onClick={() =>
                setNewTemplate({
                  name: '',
                  description: '',
                  steps: [{ title: '', offset_days: 0, channel: 'email' }],
                })
              }
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
            >
              <Plus size={16} />
              New Sequence
            </button>
          </div>
        </div>
        <p className="text-sm text-slate-400 mb-4">
          Automated follow-up task sequences for contacts
        </p>

        <div className="space-y-3">
          {templates.map((template) => (
            <div key={template.id} className="bg-slate-800/50 border border-slate-700 rounded-lg overflow-hidden">
              <button
                onClick={() =>
                  setExpandedTemplate(expandedTemplate === template.id ? null : template.id)
                }
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-800/80 transition-colors"
              >
                <div>
                  <div className="text-sm font-medium text-white flex items-center gap-2">
                    {template.name}
                    {template.is_default && (
                      <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded">
                        Default
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400">{template.steps.length} steps</div>
                </div>
                {expandedTemplate === template.id ? (
                  <ChevronUp size={18} className="text-slate-400" />
                ) : (
                  <ChevronDown size={18} className="text-slate-400" />
                )}
              </button>

              {expandedTemplate === template.id && (
                <div className="px-4 pb-4 border-t border-slate-700">
                  <div className="pt-3 space-y-2">
                    {template.steps.map((step, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-2 bg-slate-800 rounded"
                      >
                        <span className="w-6 h-6 flex items-center justify-center text-xs font-bold bg-slate-700 text-slate-300 rounded-full">
                          {idx + 1}
                        </span>
                        <div className="flex-1">
                          <div className="text-sm text-white">{step.title}</div>
                          <div className="text-xs text-slate-400">
                            Day {step.offset_days} • {step.channel}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-700">
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/10 rounded transition-colors"
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {newTemplate && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
              <TemplateEditor
                template={{
                  id: 'new',
                  team_id: team.id,
                  name: newTemplate.name,
                  description: newTemplate.description,
                  steps: newTemplate.steps,
                  is_default: false,
                  created_at: '',
                  updated_at: '',
                }}
                onSave={() => handleCreateTemplate()}
                onCancel={() => setNewTemplate(null)}
                onChange={(updates) => setNewTemplate({ ...newTemplate, ...updates })}
              />
            </div>
          )}

          {templates.length === 0 && !newTemplate && (
            <p className="text-sm text-slate-400 text-center py-4">
              No sequences configured. Click "Seed Default" for a starter template.
            </p>
          )}
        </div>
      </div>

      {/* Instantly Integration */}
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Zap size={20} className="text-orange-500" />
            Instantly Integration
          </h2>
          <button
            onClick={fetchInstantlyOptions}
            disabled={loadingInstantly}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors"
          >
            {loadingInstantly ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
            Refresh Lists
          </button>
        </div>
        <p className="text-sm text-slate-400 mb-4">
          Configure default lead list and campaign for Instantly outreach
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Default Lead List
            </label>
            <select
              value={instantlyDefaultList}
              onChange={(e) => setInstantlyDefaultList(e.target.value)}
              disabled={loadingInstantly}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Select a lead list...</option>
              {instantlyLists.map((list) => (
                <option key={list.id} value={list.id}>
                  {list.name} {list.lead_count !== undefined ? `(${list.lead_count} leads)` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">Contacts will be added to this list when pushed to Instantly</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Default Campaign
            </label>
            <select
              value={instantlyDefaultCampaign}
              onChange={(e) => setInstantlyDefaultCampaign(e.target.value)}
              disabled={loadingInstantly}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">No campaign (optional)</option>
              {instantlyCampaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name} {campaign.status ? `(${campaign.status})` : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">Optionally add contacts to a campaign when pushing</p>
          </div>

          <div className="pt-4">
            <button
              onClick={handleSaveInstantly}
              disabled={savingInstantly}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-500 disabled:bg-slate-600 text-white font-medium rounded-lg transition-colors"
            >
              {savingInstantly ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Save Instantly Settings
            </button>
          </div>

          {instantlyLists.length === 0 && !loadingInstantly && (
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-sm text-yellow-400">
              No Instantly lists found. Make sure your API key is configured and you have created lead lists in Instantly.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface RuleEditorProps {
  rule: AssignmentRule;
  members: TeamMember[];
  onSave: (updates: Partial<AssignmentRule>) => void;
  onCancel: () => void;
  onChange?: (updates: Partial<{ name: string; conditions: AssignmentRuleConditions; assign_to_user_id: string }>) => void;
  isNew?: boolean;
}

const RuleEditor: React.FC<RuleEditorProps> = ({ rule, members, onSave, onCancel, onChange, isNew }) => {
  const [name, setName] = useState(rule.name);
  const [condType, setCondType] = useState<string>(rule.conditions.type || '');
  const [condSource, setCondSource] = useState<string>(rule.conditions.source || '');
  const [assignTo, setAssignTo] = useState(rule.assign_to_user_id);

  const handleSave = () => {
    if (isNew && onChange) {
      onChange({ name, conditions: { type: condType as ContactType || undefined, source: condSource || undefined }, assign_to_user_id: assignTo });
      onSave({});
    } else {
      onSave({
        name,
        conditions: { type: condType as ContactType || undefined, source: condSource || undefined },
        assign_to_user_id: assignTo,
      });
    }
  };

  return (
    <div className="space-y-3">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Rule name"
        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
      />
      <div className="grid grid-cols-3 gap-3">
        <select
          value={condType}
          onChange={(e) => setCondType(e.target.value)}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="">Any type</option>
          {CONTACT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <input
          type="text"
          value={condSource}
          onChange={(e) => setCondSource(e.target.value)}
          placeholder="Source (optional)"
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
        <select
          value={assignTo}
          onChange={(e) => setAssignTo(e.target.value)}
          className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="">Assign to...</option>
          {members.map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {m.email || m.user_id.slice(0, 8)}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={handleSave}
          disabled={!name || !assignTo}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-500 disabled:bg-slate-600 text-white rounded-lg transition-colors"
        >
          <Save size={14} />
          Save
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
        >
          <X size={14} />
          Cancel
        </button>
      </div>
    </div>
  );
};

interface TemplateEditorProps {
  template: TaskTemplate;
  onSave: () => void;
  onCancel: () => void;
  onChange: (updates: Partial<{ name: string; description: string; steps: TaskTemplateStep[] }>) => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ template, onSave, onCancel, onChange }) => {
  const addStep = () => {
    onChange({ steps: [...template.steps, { title: '', offset_days: 0, channel: 'email' }] });
  };

  const updateStep = (idx: number, updates: Partial<TaskTemplateStep>) => {
    const newSteps = template.steps.map((s, i) => (i === idx ? { ...s, ...updates } : s));
    onChange({ steps: newSteps });
  };

  const removeStep = (idx: number) => {
    onChange({ steps: template.steps.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-3">
      <input
        type="text"
        value={template.name}
        onChange={(e) => onChange({ name: e.target.value })}
        placeholder="Sequence name"
        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
      />
      <input
        type="text"
        value={template.description || ''}
        onChange={(e) => onChange({ description: e.target.value })}
        placeholder="Description (optional)"
        className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
      />

      <div className="space-y-2">
        <div className="text-xs text-slate-400 uppercase tracking-wide">Steps</div>
        {template.steps.map((step, idx) => (
          <div key={idx} className="flex items-center gap-2 p-2 bg-slate-800 rounded-lg">
            <span className="w-6 h-6 flex items-center justify-center text-xs font-bold bg-slate-700 text-slate-300 rounded-full shrink-0">
              {idx + 1}
            </span>
            <input
              type="text"
              value={step.title}
              onChange={(e) => updateStep(idx, { title: e.target.value })}
              placeholder="Task title"
              className="flex-1 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <input
              type="number"
              value={step.offset_days}
              onChange={(e) => updateStep(idx, { offset_days: parseInt(e.target.value) || 0 })}
              placeholder="Day"
              className="w-16 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
            <select
              value={step.channel}
              onChange={(e) => updateStep(idx, { channel: e.target.value as any })}
              className="px-2 py-1.5 bg-slate-700 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {SEQUENCE_CHANNELS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <button
              onClick={() => removeStep(idx)}
              className="p-1.5 text-slate-400 hover:text-red-400 transition-colors"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          onClick={addStep}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
        >
          <Plus size={14} />
          Add Step
        </button>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button
          onClick={onSave}
          disabled={!template.name || template.steps.length === 0}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-500 disabled:bg-slate-600 text-white rounded-lg transition-colors"
        >
          <Save size={14} />
          Save Sequence
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-white transition-colors"
        >
          <X size={14} />
          Cancel
        </button>
      </div>
    </div>
  );
};

export default SettingsPage;
